import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, DollarSign, Receipt, Truck, AlertCircle, RefreshCw, Kanban, LayoutGrid,
} from "lucide-react";
import { FilterBar }    from "@/components/ui/FilterBar";
import { Card }         from "@/components/ui/Card";
import { Button }       from "@/components/ui/Button";
import { useModules }   from "@/context/ModulesContext";
import { formatCurrency, formatNumber } from "@/utils/format";
import { KanbanComprasData, type SolicitacaoCard } from "@/services/suprimentos";
import { ROUTES } from "@/config/constants";
import { useAppAccess } from "@/hooks/useAppAccess";

import {
  getAllPedidos,
  computePedidosPorMes,
  computePedidosPorCentro,
  computeStatusPedidos,
  computeEvolucaoPrecos,
  computeRankingFornecedores,
} from "./chartUtils";

import { PedidosPorMes }        from "./charts/PedidosPorMes";
import { PedidosPorCentro }     from "./charts/PedidosPorCentro";
import { PedidosPendentes }     from "./charts/PedidosPendentes";
import { EvolucaoPrecos }       from "./charts/EvolucaoPrecos";
import { RankingFornecedores }  from "./charts/RankingFornecedores";
import { RankingInsumos }       from "./charts/RankingInsumos";
import { AumentosPreco }        from "./charts/AumentosPreco";
import { PedidosPorValor }      from "./charts/PedidosPorValor";
import { filterKanbanComprasData } from "./kanbanBoardFilter";
import { PipelineSolicitacoesModal } from "./PipelineSolicitacoesModal";
import { SolicitacaoItensModal } from "./SolicitacaoItensModal";

/** Normaliza o texto digitado (aceita "SC 123" / "123") e resolve o card no Kanban, se houver. */
function resolveSolicitacaoCard(
  raw: string,
  data: KanbanComprasData | null
): SolicitacaoCard {
  const id = String(raw ?? "").trim().replace(/^sc\s*/i, "").trim();
  const all = [...(data?.solPendente ?? []), ...(data?.solAprovada ?? [])];
  const found = all.find((c) => {
    const cid = String(c.purchaseRequestId ?? "").trim().replace(/^sc\s*/i, "").trim();
    return cid === id;
  });
  if (found) return found;
  return {
    purchaseRequestId: id,
    buildingCode: "",
    buildingName: "",
    costCenterId: "",
    costCenterName: "",
    itemCount: 0,
    requestDate: "",
    maxCompetenceLevel: 0,
  };
}

/* ─── KPIs ───────────────────────────────────────────────────────────────── */
function computeKpis(data: KanbanComprasData) {
  const todos        = getAllPedidos(data);
  const quantidade   = todos.length;
  const valorTotal   = todos.reduce((s, p) => s + (p.value ?? 0), 0);
  const ticketMedio  = quantidade > 0 ? valorTotal / quantidade : 0;
  const entregues    = data.pedidosGeradoFinanceiro.length;
  const pctEntregues = quantidade > 0 ? (entregues / quantidade) * 100 : 0;
  return { quantidade, valorTotal, ticketMedio, pctEntregues };
}

/* ─── Página ─────────────────────────────────────────────────────────────── */
export function SuprimentosPage() {
  const navigate = useNavigate();
  const { can } = useAppAccess();
  const { suprimentos, ccOptions, loadingCC, ccNameMap } = useModules();
  const { values, setValues, apply, reset, autoLoad,
    data, loading, error, applied,
    orderItems, loadingItems, errorItems,
    supplierOptions,
    itemOptions,
  } = suprimentos;
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [scModal, setScModal] = useState<SolicitacaoCard | null>(null);

  /* Carrega dados na primeira visita; nas seguintes, usa o cache do contexto. */
  useEffect(() => { autoLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    const scRaw = String(values.purchaseRequestFilter ?? "").trim();
    apply();
    if (scRaw) {
      setScModal(resolveSolicitacaoCard(scRaw, data));
    } else {
      setScModal(null);
    }
  };

  const handleReset = () => {
    setScModal(null);
    reset();
  };

  const dashboardFilterValues = useMemo(
    () => ({
      ...applied,
      quotationFilter: "",
      orderFilter: "",
    }),
    [applied],
  );

  const filteredKanban = useMemo(
    () => (data ? filterKanbanComprasData(data, dashboardFilterValues) : null),
    [data, dashboardFilterValues]
  );

  /* Enriquece costCenterName a partir do ccNameMap quando o campo vem vazio do Sienge */
  const pedidos = useMemo(() => {
    const raw = filteredKanban ? getAllPedidos(filteredKanban) : [];
    return raw.map((p) => ({
      ...p,
      costCenterName:
        p.costCenterName?.trim() ||
        (p.costCenterId ? (ccNameMap.get(p.costCenterId) ?? "") : "") ||
        "",
    }));
  }, [filteredKanban, ccNameMap]);
  const isFirstLoad = loading && !data;
  const kpis        = filteredKanban ? computeKpis(filteredKanban) : null;

  /* ── Dados dos gráficos ── */
  const porMes       = useMemo(() => computePedidosPorMes(pedidos),                      [pedidos]);
  const porCentro    = useMemo(() => computePedidosPorCentro(pedidos, ccNameMap),         [pedidos, ccNameMap]);
  const statusData   = useMemo(() => (filteredKanban ? computeStatusPedidos(filteredKanban) : []), [filteredKanban]);
  const evolucao     = useMemo(() => computeEvolucaoPrecos(pedidos),                     [pedidos]);
  const fornecedores = useMemo(() => computeRankingFornecedores(pedidos),                [pedidos]);

  const hasCharts = data || loading;

  return (
    <div className="flex flex-col gap-6">

      {/* ── Cabeçalho ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Suprimentos</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Visão estratégica de pedidos de compra e insumos no período
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={apply} loading={loading}
          leftIcon={<RefreshCw className="w-4 h-4" />}>
          Atualizar
        </Button>
      </div>

      {/* ── Filtros ── */}
      <FilterBar
        values={values} onChange={setValues} onApply={handleApply} onReset={handleReset}
        costCenterOptions={ccOptions} loadingOptions={loadingCC} loading={loading}
        showSuprimentosEntityFilters
        showSuprimentosKanbanDocFilters={false}
        supplierOptions={supplierOptions}
        loadingSupplierOptions={loading}
        itemOptions={itemOptions}
        loadingItemOptions={loadingItems}
        headerEnd={
          <>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => setPipelineOpen(true)}
              leftIcon={<LayoutGrid className="w-4 h-4" />}
              className="bg-violet-600 text-white hover:bg-violet-700 hover:text-white active:bg-violet-800 shadow-sm"
            >
              Pipeline
            </Button>
            {can("kanban") ? (
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => navigate(ROUTES.SUPRIMENTOS_KANBAN)}
                leftIcon={<Kanban className="w-4 h-4" />}
                className="bg-violet-600 text-white hover:bg-violet-700 hover:text-white active:bg-violet-800 shadow-sm"
              >
                Kanban
              </Button>
            ) : null}
          </>
        }
      />

      <PipelineSolicitacoesModal
        open={pipelineOpen}
        onClose={() => setPipelineOpen(false)}
        solPendente={filteredKanban?.solPendente ?? []}
        solAprovada={filteredKanban?.solAprovada ?? []}
        itemQueryPeriod={{ startDate: applied.startDate, endDate: applied.endDate }}
        loading={isFirstLoad}
      />

      {scModal && (
        <SolicitacaoItensModal
          solicitacao={scModal}
          onClose={() => setScModal(null)}
          /* Sem período do dashboard: o backend usa a janela da própria SC (preços não dependem do filtro de datas). */
        />
      )}

      {/* ── Erro ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={apply} className="ml-auto text-red-500 hover:text-red-700 underline text-xs">
            Tentar novamente
          </button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card title="Quantidade de Pedidos"
          value={isFirstLoad ? undefined : formatNumber(kpis?.quantidade ?? 0)}
          icon={<ShoppingCart className="w-5 h-5" />} loading={isFirstLoad}
          className="border-l-4 border-l-blue-400" />
        <Card title="Valor dos Pedidos"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.valorTotal ?? 0)}
          icon={<DollarSign className="w-5 h-5" />} loading={isFirstLoad}
          className="border-l-4 border-l-brand-400" />
        <Card title="Ticket Médio"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.ticketMedio ?? 0)}
          icon={<Receipt className="w-5 h-5" />} loading={isFirstLoad}
          className="border-l-4 border-l-purple-400" />
        <Card title="% Pedidos Entregues"
          value={isFirstLoad ? undefined : `${formatNumber(kpis?.pctEntregues ?? 0, 1)}%`}
          icon={<Truck className="w-5 h-5" />} loading={isFirstLoad}
          className="border-l-4 border-l-emerald-400" />
      </div>

      {isFirstLoad && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500">Consultando o Sienge… isso pode levar alguns segundos.</p>
        </div>
      )}

      {/* ══════════════════ SEÇÃO DE ANÁLISE ESTRATÉGICA ══════════════════ */}
      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Análise Estratégica — Pedidos
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Linha 1: Pedidos por Mês + % Pendentes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <PedidosPorMes data={porMes} loading={isFirstLoad} />
            </div>
            <PedidosPendentes data={statusData} loading={isFirstLoad} />
          </div>

          {/* Linha 2: Centro de Custo + Fornecedores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <PedidosPorCentro    data={porCentro}    loading={isFirstLoad} />
            <RankingFornecedores data={fornecedores} loading={isFirstLoad} />
          </div>

          {/* Linha 3: Pedidos por Valor */}
          <PedidosPorValor pedidos={pedidos} loading={isFirstLoad} />

          {/* ══════════════════ SEÇÃO DE INSUMOS ══════════════════ */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Análise de Insumos
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Ranking de Insumos — busca via API dedicada */}
          <RankingInsumos
            orderItems={orderItems}
            loading={loadingItems}
            error={errorItems}
          />

          {/* Maiores Aumentos de Preço por Insumo */}
          <AumentosPreco
            orderItems={orderItems}
            loading={loadingItems}
            error={errorItems}
          />

          {/* Evolução de Valores por Mês — ao final da parte Suprimentos */}
          <EvolucaoPrecos data={evolucao} loading={isFirstLoad} />
        </>
      )}
    </div>
  );
}
